import React, { useEffect, useRef } from 'react';
import { HStack, Input, Text } from '@chakra-ui/react';
import { formatSignInCode, isCompleteSignInCode } from '../utils/signInCode';

interface Props {
  /** id of the FIRST box — the FormLabel's htmlFor points here, so getByLabelText finds it. */
  id: string;
  label: string;
  /** The code as `NN NN` (or fewer digits while typing). */
  value: string;
  onChange: (value: string) => void;
  /**
   * Fired once when the fourth digit lands, WITH the code — the page submits on it, so the
   * reader never has to press a button. Passing the value avoids reading state that has not
   * re-rendered yet. Fires again only after the code has changed (e.g. cleared after a wrong one).
   */
  onComplete?: (code: string) => void;
  isDisabled?: boolean;
}

const pairs = (value: string): [string, string] => {
  const digits = value.replace(/[^0-9]/g, '').slice(0, 4);
  return [digits.slice(0, 2), digits.slice(2, 4)];
};

/**
 * Two boxes, one two-digit pair each — the shape of the code in the message. No example
 * digits are shown in them (a placeholder number reads as a real code). Typing spills forward,
 * Backspace on an empty second box steps back, and a paste of `4729` / `47 29` into either box
 * fills both. Value in and out is always `NN NN`.
 */
export function SignInCodePairs({ id, label, value, onChange, onComplete, isDisabled }: Props) {
  const first = useRef<HTMLInputElement>(null);
  const second = useRef<HTMLInputElement>(null);
  const lastSubmitted = useRef('');
  const [a, b] = pairs(value);

  // After a wrong code the page clears the value and re-enables the boxes: put the cursor back
  // where the next attempt starts. (The first render is covered by autoFocus.)
  const hadAttempt = useRef(false);
  useEffect(() => {
    if (value) { hadAttempt.current = true; return; }
    lastSubmitted.current = ''; // cleared (e.g. after a wrong code): the same code may be tried again
    if (hadAttempt.current && !isDisabled) { hadAttempt.current = false; first.current?.focus(); }
  }, [isDisabled, value]);

  const emit = (next: string) => {
    const formatted = formatSignInCode(next);
    onChange(formatted);
    if (!isCompleteSignInCode(formatted)) { lastSubmitted.current = ''; return; }
    if (formatted === lastSubmitted.current) return; // the same complete code is only submitted once
    lastSubmitted.current = formatted;
    onComplete?.(formatted);
  };

  const onFirst = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '');
    // More than a pair typed or pasted here: the rest belongs to the second box.
    emit(digits.length > 2 ? digits : digits + b);
    if (digits.length >= 2) second.current?.focus();
  };
  const onSecond = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '');
    if (digits.length > 2) { emit(digits); return; } // a full code pasted into the second box
    emit(a + digits);
  };

  const boxProps = {
    inputMode: 'numeric' as const,
    autoComplete: 'one-time-code',
    maxLength: 4, // allows a paste of the whole code; digits beyond a pair spill over
    size: 'lg' as const,
    textAlign: 'center' as const,
    letterSpacing: '0.3em',
    fontFamily: 'mono',
    w: '5.5rem',
    isDisabled,
  };

  return (
    <HStack spacing={3} align="center" justify="center" w="full">
      <Input {...boxProps} id={id} ref={first} autoFocus value={a}
        onChange={e => onFirst(e.target.value)} />
      <Text fontSize="xl" color="gray.500" userSelect="none" aria-hidden>·</Text>
      <Input {...boxProps} ref={second} aria-label={`${label}, second pair`} value={b}
        onChange={e => onSecond(e.target.value)}
        onKeyDown={e => { if (e.key === 'Backspace' && !b) first.current?.focus(); }} />
    </HStack>
  );
}
